import {
  createTeamFailure,
  createTeamSucceed,
  deleteTeamFailure,
  deleteTeamSucceed,
  getTeamsFailure,
  getTeamsSucceed,
  updateTeamContributorsFailure,
  updateTeamContributorsSucceed,
  updateTeamFailure,
  updateTeamSucceed
} from '../actions';
import { LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import {
  getTeams as getTeamsApi,
  createTeam as createTeamApi,
  updateTeam as updateTeamApi,
  deleteTeam as deleteTeamApi,
  updateTeamContributors as updateTeamContributorsApi
} from 'common/services/teams.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import {
  createGroupTeam as createGroupApi,
  updateGroup as updateGroupApi,
  deleteGroup as deleteGroupApi
} from 'common/services/group-of-teams.service';
import { createTeamsWithAllteams } from 'common/utils/mockAllTeamsGroupMode';

/**
 * Fetches teams and dispatches the result
 * @param {object} options
 * @param {boolean} options.lean_team - Whether to fetch lean team data
 * @returns {Function} Thunk function
 */
export function getTeams({ lean_team = false } = {}) {
  return async dispatch => {
    try {
      const teams = await getTeamsApi({
        active_members_only: true,
        nonmerged_members_only: true,
        lean_team
      });

      // TO DO: delete this mock mode when BE of all teams group is ready
      // mock all teams group mode
      const isMockModeAllTeamsGroup = localStorage.getItem(
        LOCAL_STORAGE_KEYS.MOCK_MODE_ALL_TEAMS_GROUP
      );

      const teamsToDispatch = isMockModeAllTeamsGroup
        ? createTeamsWithAllteams(teams)
        : teams;
      if (!Array.isArray(teamsToDispatch)) {
        throw new Error('callers/teams: getTeams - teamsToDispatch is invalid');
      }

      dispatch(
        getTeamsSucceed(
          teamsToDispatch.map(team => {
            const { contributors = [], name = '' } = team;
            return {
              ...team,
              name: name.trim(),
              contributor_ids: contributors.map(({ id }) => id)
            };
          })
        )
      );

      return teamsToDispatch;
    } catch (e) {
      dispatch(getTeamsFailure(e));
      return [];
    }
  };
}

export function createTeam(params) {
  return async dispatch => {
    try {
      const team = await createTeamApi(params);
      dispatch(createTeamSucceed(team));
      return team;
    } catch (e) {
      dispatch(createTeamFailure());
      return Promise.reject(e);
    }
  };
}

export function updateTeam(params) {
  return async dispatch => {
    try {
      const team = await updateTeamApi(params);
      dispatch(updateTeamSucceed(team));

      return team;
    } catch (e) {
      dispatch(updateTeamFailure());
      throw e;
    }
  };
}

export function deleteTeam(id) {
  return async dispatch => {
    try {
      await deleteTeamApi(id);

      dispatch(deleteTeamSucceed({ id }));

      return true;
    } catch (e) {
      dispatch(deleteTeamFailure());

      return Promise.reject();
    }
  };
}

export function deleteGroup(id) {
  return async dispatch => {
    try {
      await deleteGroupApi(id);

      dispatch(deleteTeamSucceed({ id }));

      return true;
    } catch (e) {
      dispatch(deleteTeamFailure());

      return Promise.reject();
    }
  };
}

export function updateTeamContributors(
  teamId,
  contributorsToAdd,
  contributorIdsToRemove,
  with_history,
  backfill_contributors_only = false,
  isGroup = false
) {
  return async dispatch => {
    const isHardDelete = BrowserStorageAccessor().local.get(
      LOCAL_STORAGE_KEYS.SHOW_IS_THAT_YOUR_TEAM_BAR
    );
    try {
      await updateTeamContributorsApi({
        id: teamId,
        contributors_to_add: contributorsToAdd,
        contributors_ids_to_remove: contributorIdsToRemove,
        hard_delete: isHardDelete,
        with_history,
        backfill_contributors_only,
        isGroup
      });
      dispatch(
        updateTeamContributorsSucceed({
          teamId,
          contributorsToAdd,
          contributorIdsToRemove
        })
      );

      return true;
    } catch (e) {
      dispatch(updateTeamContributorsFailure());

      return false;
    }
  };
}

export function createGroup(params) {
  return async dispatch => {
    try {
      const group = await createGroupApi(params);
      dispatch(createTeamSucceed(group));
      return group;
    } catch (e) {
      dispatch(createTeamFailure());
      return Promise.reject(e);
    }
  };
}

export function updateGroup(params) {
  return async dispatch => {
    try {
      const team = await updateGroupApi(params);
      dispatch(updateTeamSucceed(team));

      return team;
    } catch (e) {
      dispatch(updateTeamFailure());
      throw e;
    }
  };
}
